import { useState, type ReactNode } from "react";

import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Stat,
  statusVariant,
} from "@blog/ui";

import { t } from "../lib/i18n";
import { useApi } from "../app/ApiContext";
import { PageHeader } from "../app/Shell";
import { formatDateTime, formatRelative } from "../lib/format";
import { useAsync, useSite } from "../lib/hooks";

/**
 * Dashboard (TASK-801, SPEC §39).
 *
 * Shows what an editor opens the CMS to find out: how much is published, what
 * is in flight, whether the pipeline is healthy, and what changed recently.
 *
 * Everything comes from one `GET /github/status` plus one audit query — a
 * dashboard that fires eight requests is slow for no reason, and the counts are
 * already computed server-side.
 */
export function Dashboard() {
  const client = useApi();
  const { site, loading: siteLoading, error: siteError } = useSite(client);
  const siteId = site?.id;

  const status = useAsync(async () => {
    if (siteId === undefined) {
      return null;
    }

    return client.getGitHubStatus(siteId);
  }, [siteId]);

  if (siteLoading) {
    return <p className="text-sm text-neutral-500">{t("common.loading")}</p>;
  }

  if (siteError !== null) {
    return (
      <Card>
        <CardContent>
          <p className="text-sm text-red-600">{siteError}</p>
        </CardContent>
      </Card>
    );
  }

  if (site === null) {
    return (
      <Card>
        <CardContent>
          <p className="text-sm text-neutral-600">{t("dashboard.noSite")}</p>
        </CardContent>
      </Card>
    );
  }

  const entries = status.data?.content.entries ?? {};
  const localizations = status.data?.content.localizations ?? {};
  const deployment = status.data?.latestDeployment ?? null;

  const published = localizations.published ?? 0;
  const drafts = localizations.draft ?? 0;
  const scheduled = localizations.scheduled ?? 0;

  return (
    <>
      <PageHeader title={t("dashboard.title")} description={`${site.name} · ${site.domain}`} />

      {status.error === null ? null : (
        <Card className="mb-4">
          <CardContent>
            <p className="text-sm text-red-600">{status.error}</p>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        <Stat label={t("dashboard.published")} value={published} />
        <Stat label={t("dashboard.drafts")} value={drafts} />
        <Stat label={t("dashboard.scheduled")} value={scheduled} />
        <Stat
          label={t("dashboard.entries")}
          value={(entries.post ?? 0) + (entries.page ?? 0)}
          hint={t("dashboard.entriesHint", {
            posts: entries.post ?? 0,
            pages: entries.page ?? 0,
          })}
        />
      </div>

      <div className="mt-6 grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>GitHub</CardTitle>
            <CardDescription>
              {status.data?.github.owner ?? site.githubOwner}/{status.data?.github.repo ?? site.githubRepo}
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-2 text-sm">
            <Row label={t("common.webhook")}>
              <Badge variant={status.data?.github.webhookConfigured === true ? "success" : "warning"}>
                {status.data?.github.webhookConfigured === true ? t("common.configured") : t("common.notConfigured")}
              </Badge>
            </Row>
            <Row label={t("common.lastSync")}>
              {status.data?.github.lastSyncAt == null
                ? t("common.never")
                : formatRelative(status.data.github.lastSyncAt, Date.now())}
            </Row>
            <Row label={t("common.installation")}>{site.githubInstallationId}</Row>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t("dashboard.latestDeployment")}</CardTitle>
            <CardDescription>{t("dashboard.deploymentDesc")}</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-2 text-sm">
            {deployment === null ? (
              <p className="text-neutral-500">{t("dashboard.noDeployments")}</p>
            ) : (
              <>
                <Row label={t("common.status")}>
                  <Badge
                    variant={
                      deployment.status === "success"
                        ? "success"
                        : deployment.status === "failed"
                          ? "destructive"
                          : "warning"
                    }
                  >
                    {deployment.status}
                  </Badge>
                </Row>
                <Row label={t("dashboard.commit")}>{deployment.commitSha ?? "—"}</Row>
                <Row label={t("dashboard.when")}>{formatDateTime(deployment.createdAt)}</Row>
                {deployment.error === undefined ? null : (
                  <p className="mt-1 text-xs text-red-600">{deployment.error}</p>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>

      <Card className="mt-4">
        <CardHeader>
          <CardTitle>{t("dashboard.recentActivity")}</CardTitle>
          <CardDescription>
            {status.data?.github.lastActivityAt == null
              ? t("dashboard.nothingRecorded")
              : t("dashboard.lastChange", {
                  time: formatRelative(status.data.github.lastActivityAt, Date.now()),
                })}
          </CardDescription>
        </CardHeader>
        <CardContent className="text-sm text-neutral-600">
          <p>
            {t("dashboard.theme")}{" "}
            <Badge variant={statusVariant("draft")}>{site.themeId} {site.themeVersion}</Badge>
          </p>
          <p className="mt-2">
            {t("dashboard.locales", {
              locales: site.supportedLocales.join(", "),
              default: site.defaultLocale,
            })}
          </p>
        </CardContent>
      </Card>

      <PasswordCard />
    </>
  );
}

/**
 * Credential rotation for the current deployment (SPEC §58).
 *
 * The worker cannot change its own environment secret, so the rotatable
 * credential is CMS-managed in D1 (PBKDF2-hashed); the deployment's
 * break-glass secret keeps working either way. Requiring the current password
 * here is what stops an abandoned tab from rotating what it cannot name.
 */
function PasswordCard() {
  const client = useApi();

  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function submit(): Promise<void> {
    if (next !== confirm) {
      setError(t("dashboard.passwordMismatch"));
      return;
    }

    if (next.length < 8) {
      setError(t("dashboard.passwordTooShort"));
      return;
    }

    setBusy(true);
    setMessage(null);
    setError(null);

    try {
      await client.changePassword({ currentPassword: current, newPassword: next });
      setMessage(t("dashboard.passwordChanged"));
      setCurrent("");
      setNext("");
      setConfirm("");
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t("github.syncFailed"));
    } finally {
      setBusy(false);
    }
  }

  const field =
    "h-9 w-full rounded-md border border-neutral-300 bg-white px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-400";

  return (
    <Card className="mt-4">
      <CardHeader>
        <CardTitle>{t("dashboard.passwordTitle")}</CardTitle>
        <CardDescription>{t("dashboard.passwordDesc")}</CardDescription>
      </CardHeader>
      <CardContent>
        <form
          className="flex max-w-md flex-col gap-3"
          onSubmit={(event) => {
            event.preventDefault();
            void submit();
          }}
        >
          <input
            type="password"
            className={field}
            autoComplete="current-password"
            aria-label={t("dashboard.currentPassword")}
            placeholder={t("dashboard.currentPassword")}
            value={current}
            onChange={(event) => {
              setCurrent(event.target.value);
            }}
          />
          <input
            type="password"
            className={field}
            autoComplete="new-password"
            aria-label={t("dashboard.newPassword")}
            placeholder={t("dashboard.newPassword")}
            value={next}
            onChange={(event) => {
              setNext(event.target.value);
            }}
          />
          <input
            type="password"
            className={field}
            autoComplete="new-password"
            aria-label={t("dashboard.confirmPassword")}
            placeholder={t("dashboard.confirmPassword")}
            value={confirm}
            onChange={(event) => {
              setConfirm(event.target.value);
            }}
          />
          <div className="flex items-center gap-3">
            <Button type="submit" size="sm" loading={busy}>
              {busy ? t("dashboard.updating") : t("dashboard.updatePassword")}
            </Button>
            {message !== null && <p className="text-sm text-green-700">{message}</p>}
            {error !== null && <p className="text-sm text-red-600">{error}</p>}
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

function Row({ label, children }: { readonly label: string; readonly children: ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <span className="text-neutral-500">{label}</span>
      <span className="font-medium text-neutral-800">{children}</span>
    </div>
  );
}
